import MobxReactForm from 'mobx-react-form';
import { observable } from 'mobx';
import dvr from 'mobx-react-form/lib/validators/DVR';
import validatorjs from 'validatorjs';
import AuthStore from '../../../../stores/AuthStore';
import ProfileState from '../../../../stores/ProfileStore';
import { GENDERS, TITLES } from '../../../../framework/constants/profile';
import NotificationStore from '../../../../stores/NotificationStore';
import { PHONE_PREFIX_DEFAULT, PHONE_PREFIXES } from '../../../../framework/constants/forms';
import i18next from 'i18next';
const t = (key, options) => i18next.t(key, options);
const plugins = {
    dvr: dvr({
        package: validatorjs
    })
};
const fields = [
    {
        name: 'privateAddress',
        fields: [
            {
                name: 'gender',
                placeholder: 'forms.contactForm.placeholdersRequired.gender',
                rules: 'required',
                extra: GENDERS,
                output: (valueObj) => valueObj.value
            },
            {
                name: 'email',
                type: 'email',
                label: 'Email',
                placeholder: 'forms.credentialsForm.placeholders.email',
                rules: 'required|email'
            },
            {
                name: 'birthday',
                label: 'forms.contactForm.placeholdersRequired.birthday',
                rules: 'dd/MM/yyyy',
                value: null,
                output: (value) => {
                    if (!value)
                        return null;
                    // DatePicker builds a local-midnight Date; re-anchor to UTC midnight
                    // on the same calendar date so JSON serialization can't roll it back a day.
                    if (value instanceof Date) {
                        return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())).toISOString();
                    }
                    return value;
                }
            },
            {
                name: 'title',
                placeholder: 'forms.contactForm.placeholdersRequired.title',
                extra: TITLES,
                output: (valueObj) => {
                    return valueObj ? valueObj.value : null;
                }
            },
            {
                name: 'firstName',
                label: 'forms.contactForm.placeholdersRequired.firstName',
                rules: 'required|string'
            },
            {
                name: 'lastName',
                label: 'forms.contactForm.placeholdersRequired.lastName',
                rules: 'required|string'
            },
            {
                name: 'phoneMain',
                label: 'modals:offerRequest.phoneMain',
                placeholder: 'modals:offerRequest.phoneMainPlaceholder',
                rules: 'required',
                fields: [
                    {
                        name: 'countryCode',
                        placeholder: 'forms.contactForm.placeholdersRequired.countryCode',
                        extra: PHONE_PREFIXES,
                        value: PHONE_PREFIX_DEFAULT,
                        rules: 'required',
                        output: (data) => {
                            return data.value ? data.value : data;
                        }
                    },
                    {
                        name: 'areaCode',
                        label: 'forms.contactForm.placeholdersRequired.areaCode',
                        type: 'tel',
                        rules: 'required|phone_areaCode'
                    },
                    {
                        name: 'phoneNumber',
                        label: 'forms.contactForm.placeholdersRequired.phoneNumber',
                        type: 'tel',
                        rules: 'required|phone_phoneNumber'
                    }
                ],
                output: (value) => {
                    const { countryCode, areaCode, phoneNumber } = value;
                    return `${countryCode ? countryCode : ''}${areaCode}${phoneNumber}`;
                }
            }
        ]
    }
];
// The Konto tab's Abbrechen/Speichern are enabled only while the form differs from the saved
// profile. mobx-react-form's `changed` counts edits and never goes back, so editing a field and
// typing the old value again kept both buttons enabled; compare against a snapshot instead.
const ACCOUNT_FIELDS = [
    'gender', 'title', 'firstName', 'lastName', 'email', 'birthday',
    'phoneMain.countryCode', 'phoneMain.areaCode', 'phoneMain.phoneNumber'
];
const normalizeValue = (value) => {
    if (value === null || value === undefined)
        return '';
    if (value instanceof Date)
        return isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
    if (typeof value === 'object')
        return 'value' in value ? normalizeValue(value.value) : JSON.stringify(value);
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}T/.test(text) ? text.slice(0, 10) : text;
};
const accountSnapshot = (form) => JSON.stringify(ACCOUNT_FIELDS.map((name) => normalizeValue(form.$(`privateAddress.${name}`).value)));
const accountBaseline = observable.box(null);
/** remember the form's current values as the saved state (after loading or saving the profile) */
export const saveAccountBaseline = () => accountBaseline.set(accountSnapshot(form));
/** forget the saved state (the page unmounts and resets the form) */
export const clearAccountBaseline = () => accountBaseline.set(null);
/** true while the form still holds the saved values, or while the profile is not loaded yet */
export const isAccountUnchanged = () => {
    const baseline = accountBaseline.get();
    return baseline === null || baseline === accountSnapshot(form);
};
const hooks = {
    onSuccess(form) {
        const { privateAddress: { email, ...values } } = form.values();
        const emailField = form.get().privateAddress.fields.email;
        const { birthday, ...restValues } = values;
        return ProfileState.updateUserProfileData({ birthday, privateAddress: restValues })
            .then(() => {
            if (emailField.changed) {
                return AuthStore.changeEmail(emailField.value);
            }
        })
            .then(() => {
            return ProfileState.getProfileData();
        })
            .then(() => saveAccountBaseline())
            .catch((error) => {
            const { errors } = error;
            // TODO: Install jsonapi de-serialaizer and use it everywhere in app.
            // TODO: Move this part of code somewhere to the upper level of the abstraction
            if (errors) {
                errors.forEach((error) => {
                    if (error.status === '500') {
                        NotificationStore.addNotification({
                            message: t('Notifications:errors.serverError'),
                            level: 'error'
                        });
                        return;
                    }
                    const pointer = error.source && error.source.pointer;
                    const invalidationText = error.detail;
                    if (!pointer)
                        return;
                    const fieldName = pointer.split('/')[(pointer.split('/').length - 1)];
                    if (pointer.indexOf('privateAddress') >= 0) {
                        NotificationStore.addNotification({
                            message: `Contact Address ${fieldName} - ${invalidationText}`
                        });
                        form.$(`privateAddress.${fieldName}`).invalidate(invalidationText);
                    }
                    if (pointer.indexOf('deliveryAddress') >= 0) {
                        NotificationStore.addNotification({
                            message: `Delivery Address ${fieldName} - ${invalidationText}`
                        });
                        form.$(`deliveryAddress.${fieldName}`).invalidate(invalidationText);
                    }
                });
                return;
            }
            // TODO: handle jsonapi validation errors
            // when CORS issues fixed.
            // TODO: install some jsonapi client library
            NotificationStore.addNotification({
                message: t('Notifications:errors.validationError')
            });
        });
    },
    onError(form) {
        console.log('Form errors', form.errors());
    }
};
const options = {
    validateOnChange: true,
    validateOnInit: false
};
const form = new MobxReactForm({ fields }, { plugins, hooks, options });
export default form;
